import { useCallback, useContext, useEffect, useRef, useState, useMemo, memo } from "react";
import Central from "../components/Central";
import Articles from "../layouts/Articles";
import Categories from "../layouts/Categories";
import ArticlesContext from "../context/ArticlesContext";
import { useLocation } from "react-router-dom";
import { addCategoryNamesToArticles, loadArticles } from "../services/ArticleService";
import { MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID, MODAL_MESSAGE_ERROR, MODAL_TYPE_ERROR, NUMBER_OF_ARTICLES_IN_CENTRAL, NUMBER_OF_ARTICLES_ON_PAGE } from "../HistoriografijaConstants";
import { useModalContext } from "../context/ModalContext";
import { cleanName } from "../services/CommonService";
import PaginationContext from "../context/PaginationContext";
import { setError, setIsNextButtonDisabled, setIsPreviousButtonDisabled, setLoading, setReachedFirstPage } from "../context/ArticlesContextActions";
import Sidebar from "../components/Sidebar";
import { Helmet } from "react-helmet";

const Pocetna = memo(function Pocetna() {
    const paginatedArticlesRef = useRef()
    const { state, dispatch, refreshArticlesContext } = useContext(ArticlesContext)
    const {cursor, setCursor, direction, setDirection, lastUrlLocation, setLastUrlLocation} = useContext(PaginationContext)
    const useLoc = useLocation()
    const location = useMemo(() => useLoc, [useLoc])
    const { displayMessageModalWithParameters, displayGenericErrorModal } = useModalContext()

    const [pagination, setPagination] = useState({ paginatedArticles: [], totalNumber: 0 })
    const [filterValues, setFilterValues] = useState({
        selectedCategory: "",
        selectedCategoryId: null,
        selectedSubCategory: "",
        selectedSubCategoryId: null,
        keyword: "",
    })

    const memoisedPagination = useMemo(() => pagination, [pagination])
    const memoisedFilterValues = useMemo(() => filterValues, [filterValues])

    const isInternalPagination = useRef(false);

    const scrollToPaginatedArticles = useCallback(() => {
        if (paginatedArticlesRef.current) {
            paginatedArticlesRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [])

    const handleException = useCallback((error) => {
        dispatch(setError(MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID))
        if (error?.response?.status === 404) {
            displayMessageModalWithParameters(MODAL_MESSAGE_ERROR, MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID, MODAL_TYPE_ERROR)
        } else displayGenericErrorModal()
        return
    }, [])

    const refreshFilteredArticles = useCallback(async (cursor, categoryId, subcategoryId, keyword) => {
        setCursor(cursor)
        setDirection(0)
        dispatch(setLoading(true))
        return loadArticles(cursor, 0, categoryId, subcategoryId, keyword, null)
        .then(data => {
            dispatch(setError(null))
            dispatch(setLoading(false))
            setPagination({
                paginatedArticles: addCategoryNamesToArticles(state?.categories, data.content),
                totalNumber: data.totalNumber
            })
            if (data.totalNumber <= NUMBER_OF_ARTICLES_ON_PAGE) {
                dispatch(setIsNextButtonDisabled(true))
            } else {
                dispatch(setIsNextButtonDisabled(false))
            }
            dispatch(setIsPreviousButtonDisabled(true))
            dispatch(setReachedFirstPage(false))
        })
        .catch(error => handleException(error))
    }, [state?.categories, dispatch, handleException, setCursor, setDirection, setPagination])

    const loadNextArticles = useCallback(async (cursor, categoryId, subcategoryId, keyword, author=null) => {
        isInternalPagination.current = true;
        setCursor(cursor)
        setDirection(0)
        dispatch(setLoading(true))
        return loadArticles(cursor, 0, categoryId, subcategoryId, keyword, null)
        .then(data => {
            dispatch(setError(null))
            dispatch(setLoading(false))
            setPagination({
                paginatedArticles: addCategoryNamesToArticles(state?.categories, data.content),
                totalNumber: data.totalNumber
            })
            if (data.totalNumber <= NUMBER_OF_ARTICLES_ON_PAGE) {
                dispatch(setIsNextButtonDisabled(true))
            } else {
                dispatch(setIsNextButtonDisabled(false))
            }
        })
        .catch(error => handleException(error))
    }, [state?.categories, dispatch, handleException, setCursor, setDirection, setPagination])

    const loadPreviousArticles = useCallback(async (cursor, categoryId, subcategoryId, keyword, author=null) => {
        isInternalPagination.current = true;
        setCursor(cursor)
        setDirection(1)
        dispatch(setLoading(true))
        return loadArticles(cursor, 1, categoryId, subcategoryId, keyword, null)
        .then(articlesResponse => {
            dispatch(setLoading(false))
            // If the paginated articles are filtered articles, they should not depend on articles in the central part
            const filteredArticles = categoryId || keyword
            setTimeout(() => scrollToPaginatedArticles(), 100)
            if (!filteredArticles) {
                if (articlesResponse.totalNumber === NUMBER_OF_ARTICLES_ON_PAGE + NUMBER_OF_ARTICLES_IN_CENTRAL) {
                    dispatch(setIsPreviousButtonDisabled(true))
                    dispatch(setReachedFirstPage(true))
                    setPagination({
                        paginatedArticles: addCategoryNamesToArticles(state?.categories, articlesResponse.content.reverse()),
                        totalNumber: articlesResponse.totalNumber
                    })
                    return
                } else if (articlesResponse.totalNumber < NUMBER_OF_ARTICLES_ON_PAGE + NUMBER_OF_ARTICLES_IN_CENTRAL) {
                    // Previous page should always have NUMBER_OF_ARTICLES_ON_PAGE or more articles
                    // If it has less, it means that an article has been added/deleted in the meantime while the user was going through the pages.
                    // We need to refresh the central and paginated articles if paginated articles are not filtered
                    refreshArticlesContext()
                    refreshFilteredArticles(
                        state?.centralArticles[
                            state?.centralArticles?.length - 1
                        ]?.id, 
                        categoryId, 
                        subcategoryId, 
                        keyword
                    )
                    return
                }
            } else {
                if (articlesResponse.totalNumber === NUMBER_OF_ARTICLES_ON_PAGE) {
                    dispatch(setIsPreviousButtonDisabled(true))
                    dispatch(setReachedFirstPage(true))
                    setPagination({
                        paginatedArticles: addCategoryNamesToArticles(state?.categories, articlesResponse.content.reverse()),
                        totalNumber: articlesResponse.totalNumber
                    })
                    return
                } else if (articlesResponse.totalNumber < NUMBER_OF_ARTICLES_ON_PAGE) {
                    // Previous page should always have NUMBER_OF_ARTICLES_ON_PAGE or more articles
                    // If it has less, it means that an article has been added/deleted in the meantime while the user was going through the pages.
                    // We need to refresh the paginated articles if paginated articles are filtered
                    refreshFilteredArticles(0, categoryId, subcategoryId, keyword)
                    return
                }
            }
            // There are more articles on the Previous page, so enable the button
            dispatch(setIsPreviousButtonDisabled(false))
            setPagination({
                paginatedArticles: addCategoryNamesToArticles(state?.categories, articlesResponse.content.reverse()),
                totalNumber: articlesResponse.totalNumber
            })
        })
        .catch(error => handleException(error))
    }, [state?.categories, state?.centralArticles, dispatch, handleException, setCursor, setDirection, refreshArticlesContext, refreshFilteredArticles,
    scrollToPaginatedArticles, setPagination])

    useEffect(() => {
        if (isInternalPagination.current) {
            isInternalPagination.current = false; // Reset for next time
            return;
        }
        async function fetchData () {
            const currentUrlLocation = location.pathname
            const pathSegments = currentUrlLocation.split("/").filter(segment => segment !== "")
            let length = pathSegments.length
            if (length === 0 || (length === 1 && pathSegments[0] === '/')) {
                const queryParams = new URLSearchParams(location.search)
                if (queryParams.has("q")) {
                    // Filtering by keyword from the query parameter
                    setFilterValues({
                        keyword: queryParams.get("q")
                    })
                    setTimeout(() => scrollToPaginatedArticles(), 100)
                    if (lastUrlLocation !== currentUrlLocation + location.search) {
                        // the url changed since the last render, reset pagination parameters
                        setLastUrlLocation(currentUrlLocation + location.search)
                        return refreshFilteredArticles(0, null, null, queryParams.get("q"))
                    }
                    else {
                        return direction === 0 ? loadNextArticles(cursor, null, null, queryParams.get("q")) : loadPreviousArticles(cursor, null, null, queryParams.get("q"))
                    }
                } else {
                    // Initial load - no filters
                    setFilterValues({
                        selectedCategory: "",
                        selectedCategoryId: null,
                        selectedSubCategory: "",
                        selectedSubCategoryId: null,
                        keyword: "",
                    })
                    if (lastUrlLocation !== currentUrlLocation) {
                        // the url changed since the last render, reset pagination parameters
                        setLastUrlLocation(currentUrlLocation)
                        // FETCH FROM CONTEXT IF AVAILABLE
                        if (state?.centralArticles) {
                            setPagination({
                                paginatedArticles: state.centralArticles,
                                totalNumber: state.centralArticles.length
                            });
                            setCursor(state?.centralArticles[state?.centralArticles?.length - 1]?.id)
                            setDirection(0)
                            dispatch(setIsNextButtonDisabled(false))
                            dispatch(setIsPreviousButtonDisabled(true))
                            dispatch(setReachedFirstPage(false))
                            return;
                        }
                        return refreshFilteredArticles(state?.centralArticles[state?.centralArticles?.length - 1]?.id, null, null, null)
                    }
                    else return direction === 0 ? loadNextArticles(cursor, null, null, null) : loadPreviousArticles(cursor, null, null, null)
                }
            } else if (length > 0 && state && state?.categories && state?.categories.length > 0) {
                if (pathSegments[0] !== 'authors') {
                    // Filtering by category (and subcategory optionally)
                    let selectedCategoryName = pathSegments[0]
                    let category = state?.categories.find(category => cleanName(category.name) === selectedCategoryName)
                    let selectedCategoryId = category.id
                    let selectedSubCategoryName = ''
                    let selectedSubCategory = null
                    let selectedSubCategoryId = null
                    if (selectedCategoryId && length === 2) {
                        selectedSubCategoryName = pathSegments[1]
                        selectedSubCategory = category.subcategories?.find(subcat => cleanName(subcat.name) === selectedSubCategoryName)
                        selectedSubCategoryId = selectedSubCategory?.id
                    }
                    setFilterValues({
                        selectedCategory: category.name,
                        selectedCategoryId: selectedCategoryId,
                        selectedSubCategory: selectedSubCategory?.name,
                        selectedSubCategoryId: selectedSubCategoryId,
                    })
                    setTimeout(() => scrollToPaginatedArticles(), 100)
                    if (lastUrlLocation !== currentUrlLocation) {
                        // the url changed since the last render, reset pagination parameters
                        setLastUrlLocation(currentUrlLocation)
                        return refreshFilteredArticles(0, selectedCategoryId, selectedSubCategoryId, null)
                    }
                    else {
                        setTimeout(() => scrollToPaginatedArticles(), 100)
                        return direction === 0 ? loadNextArticles(cursor, selectedCategoryId, selectedSubCategoryId, null) : loadPreviousArticles(cursor, selectedCategoryId, selectedSubCategoryId, null)
                    }
                }
            }
        }
        if (state?.categories && state?.categories.length !== 0) {
            fetchData()
        }
    }, [location.pathname, location.search, state?.categories, state?.centralArticles])

    return (
        <div className="root-div">
            <Helmet>
                <title>Historiografija.ba</title>
                <meta
                    name="description"
                    content="Portal historiografija.ba će prezentirati historiografiju u Bosni i Hercegovini svijetu, kao i približavati historiografiju u svijetu Bosni i Hercegovini."
                />
            </Helmet>
            <Central />
            <Categories />
            <div className="flex-container space-around column-in-mobile">
                <Articles ref={paginatedArticlesRef} filterValues={memoisedFilterValues} paginatedArticles={memoisedPagination.paginatedArticles}
                 totalNumber={memoisedPagination.totalNumber} reloadFunction={refreshFilteredArticles} loadNextArticles={loadNextArticles}
                 loadPreviousArticles={loadPreviousArticles} />
                <Sidebar />
            </div>
        </div>
    )
})

export default Pocetna;